'use client';

import { Container } from '@repo/web-chrome/components/container';
import { Heading, Subheading } from '@repo/web-chrome/components/text';
import { useState } from 'react';
import { AppStoreBadgeRow } from '../sections/app-store-badge-row';

interface IExamTryClipDemo {
  eyebrow: string;
  heading: string;
  description: string;
  chunkLabel: string;
  revealLabel: string;
  moreLabel: string;
  ctaText: string;
}

interface IExamTryClipProps {
  demo: IExamTryClipDemo;
}

interface ISampleChunk {
  chunk: string;
  context: string;
}

const sampleChunks: ISampleChunk[] = [
  { chunk: 'on the whole', context: 'A common conversational opener.' },
  { chunk: "I'd say", context: 'Hedge phrase examiners reward as fluency.' },
  { chunk: 'it can be argued that', context: 'Academic writing opener.' },
  { chunk: 'having said that', context: 'Listening signposting cue.' },
];

export function ExamTryClip({ demo }: IExamTryClipProps): React.JSX.Element {
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const current = sampleChunks[index] ?? sampleChunks[0];

  function showAnswer(): void {
    setRevealed(true);
  }
  function nextOne(): void {
    setIndex((p) => (p + 1) % sampleChunks.length);
    setRevealed(false);
  }

  return (
    <Container className="py-16 sm:py-20">
      <span id="try-clip" aria-hidden className="-mt-16 block pt-16" />
      <div data-reveal="up" className="max-w-3xl">
        <Subheading>{demo.eyebrow}</Subheading>
        <Heading as="h2" className="mt-2">
          {demo.heading}
        </Heading>
        <p className="mt-4 text-base text-muted-foreground leading-relaxed sm:text-lg">
          {demo.description}
        </p>
      </div>
      <div
        data-reveal="up"
        className="mt-10 grid grid-cols-1 gap-6 rounded-3xl bg-card p-8 ring-1 ring-foreground/10 lg:grid-cols-[1fr_auto]"
      >
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
            {demo.chunkLabel}
          </p>
          <p className="mt-2 font-display text-3xl font-medium tracking-tight text-foreground sm:text-4xl">
            &ldquo;{current?.chunk}&rdquo;
          </p>
          {revealed && (
            <p className="mt-4 max-w-xl text-sm text-foreground/80 leading-relaxed sm:text-base">
              {current?.context}
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-2">
          {!revealed ? (
            <button
              type="button"
              onClick={showAnswer}
              className="cm-press rounded-xl bg-foreground px-5 py-3 text-sm font-medium text-background hover:opacity-90"
            >
              {demo.revealLabel}
            </button>
          ) : (
            <button
              type="button"
              onClick={nextOne}
              className="cm-press rounded-xl bg-foreground px-5 py-3 text-sm font-medium text-background hover:opacity-90"
            >
              {demo.moreLabel}
            </button>
          )}
        </div>
      </div>
      <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">{demo.ctaText}</p>
        <AppStoreBadgeRow />
      </div>
    </Container>
  );
}
